/**
 * The product preview shown on the public landing page.
 *
 * Plan §4.1 asks the landing page to show the real product rather than a
 * decorative dashboard, and §4.4/§9 forbid anything that could be mistaken for
 * a visitor's own data. Both hold at once only if the preview is built from
 * content that is *labelled* example everywhere it appears — which is why this
 * is a named, typed constant rather than numbers inlined into the markup, and
 * why every row carries the same `ExampleBadge` the workspace uses.
 *
 * Nothing here is personal, and nothing here can become personal: no visitor is
 * signed in on this route, no query reads this module, and the values are
 * machine shapes rather than balances, spend, or activity.
 */

export interface PreviewMachine {
  id: string
  /** The machine shape, as a tenant reads it. Never a host or a chain name. */
  name: string
  summary: string
  vcpu: string
  memory: string
  storage: string
  region: string
}

/**
 * The same three shapes the guided workspace explores, so a visitor who clicks
 * through recognises the screen they arrive at. Agent 2 owns the workspace's own
 * fixture; if the two ever need to be one list, that consolidation belongs to
 * integration, not to a cross-branch import.
 */
export const PREVIEW_MACHINES: readonly PreviewMachine[] = [
  {
    id: 'standard-s2',
    name: 'Standard S2',
    summary: 'Balanced compute for apps and web services.',
    vcpu: '2',
    memory: '4 GB',
    storage: '80 GB SSD',
    region: 'Global',
  },
  {
    id: 'compute-c4',
    name: 'Compute C4',
    summary: 'High performance for compute workloads.',
    vcpu: '4',
    memory: '8 GB',
    storage: '160 GB SSD',
    region: 'Global',
  },
  {
    id: 'memory-m8',
    name: 'Memory M8',
    summary: 'Optimised for memory-intensive workloads.',
    vcpu: '8',
    memory: '32 GB',
    storage: '200 GB SSD',
    region: 'Global',
  },
]

export interface PreviewStep {
  ordinal: number
  title: string
  detail: string
}

/** Explore → Connect → Verify → Fund → Rent, in the workspace's own words. */
export const PREVIEW_STEPS: readonly PreviewStep[] = [
  { ordinal: 1, title: 'Explore', detail: 'Try examples' },
  { ordinal: 2, title: 'Connect', detail: 'Link your wallet' },
  { ordinal: 3, title: 'Verify', detail: 'Confirm identity' },
  { ordinal: 4, title: 'Fund', detail: 'Add money' },
  { ordinal: 5, title: 'Rent', detail: 'Launch your own' },
]
